import { useState } from 'react';
import { accountsApi } from '../../api/services';
import type { AccountDto, AccountTreeNodeDto, CreateAccountDto } from '../../api/types';
import {
  Badge, Button, Check, Field, Form, Input, Loadable, Modal, PageHeader, Pager, Select, Table, Tabs, Toolbar, useAction, useAsync, useForm,
} from '../../ui/components';
import { CurrencySelect, invalidateAccountCache } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { useCompany } from '../../ui/company';

export function AccountsPage() {
  const [tab, setTab] = useState<'list' | 'tree'>('list');
  const [text, setText] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<AccountDto | 'new' | null>(null);
  const [parentForNew, setParentForNew] = useState<string | null>(null);
  const list = useAsync(() => accountsApi.search({ text: text.trim() || undefined, page, pageSize: 50 }), [text, page]);
  const tree = useAsync(() => accountsApi.tree(), [], tab === 'tree');
  const { run, busy } = useAction();

  const refresh = () => { list.reload(); if (tab === 'tree') tree.reload(); };

  return (
    <>
      <PageHeader title="Catálogo de cuentas" actions={<Button kind="primary" onClick={() => { setParentForNew(null); setEditing('new'); }}>Nueva cuenta</Button>} />
      <PageHelp page="accounts" />
      <Tabs tabs={[{ key: 'list', label: 'Lista' }, { key: 'tree', label: 'Árbol' }]} active={tab} onChange={setTab} />
      {tab === 'list' && (
        <>
          <Toolbar><Input value={text} onChange={(v) => { setText(v); setPage(1); }} placeholder="Buscar por número o nombre" /></Toolbar>
          <Loadable state={list}>
            {(r) => (
              <>
                <Table rows={r.items} rowKey={(a) => a.number} onRowClick={(a) => setEditing(a)} columns={[
                  { key: 'n', title: 'Cuenta', render: (a) => <code>{a.number}</code> },
                  { key: 'm', title: 'Nombre', render: (a) => <span style={{ paddingLeft: (a.levelCode - 1) * 12 }}>{a.name}</span> },
                  { key: 'l', title: 'Nivel', render: (a) => a.levelName ?? a.levelCode },
                  { key: 't', title: 'Tipo', render: (a) => (a.type === 'M' ? 'Madre' : 'Detalle') },
                  { key: 'c', title: 'Clasificación', render: (a) => a.classificationName ?? a.classificationCode },
                  { key: 'mo', title: 'Moneda', render: (a) => a.currencyName ?? a.currencyId },
                  { key: 's', title: 'Estado', render: (a) => <Badge tone={a.status === 'I' ? 'bad' : 'ok'}>{a.status === 'I' ? 'Inactiva' : 'Activa'}</Badge> },
                  {
                    key: 'x', title: '', render: (a) => (
                      <span onClick={(e) => e.stopPropagation()}>
                        {a.type === 'M' && <Button kind="link" onClick={() => { setParentForNew(a.number); setEditing('new'); }}>+ hija</Button>}
                        <Button kind="link" busy={busy} onClick={() => run(() => accountsApi.setStatus(a.number, a.status === 'I').finally(invalidateAccountCache), { success: 'Estado actualizado.', confirm: `${a.status === 'I' ? 'Activar' : 'Inactivar'} la cuenta ${a.number}?` }).then(refresh)}>
                          {a.status === 'I' ? 'Activar' : 'Inactivar'}
                        </Button>
                      </span>
                    ),
                  },
                ]} />
                <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={setPage} />
              </>
            )}
          </Loadable>
        </>
      )}
      {tab === 'tree' && <Loadable state={tree}>{(nodes) => <ul className="acc-tree">{nodes.map((n) => <TreeNode key={n.number} node={n} />)}</ul>}</Loadable>}
      {editing && <AccountForm account={editing === 'new' ? null : editing} parent={parentForNew} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </>
  );
}

function TreeNode({ node }: { node: AccountTreeNodeDto }) {
  const [open, setOpen] = useState(node.levelCode < 2);
  return (
    <li>
      <span className={`acc-tree-item${node.status === 'I' ? ' acc-muted' : ''}`} onClick={() => setOpen(!open)}>
        {node.children.length > 0 ? (open ? '▾ ' : '▸ ') : '• '}<code>{node.number}</code> {node.name}
      </span>
      {open && node.children.length > 0 && <ul>{node.children.map((c) => <TreeNode key={c.number} node={c} />)}</ul>}
    </li>
  );
}

function AccountForm({ account, parent, onClose, onSaved }: { account: AccountDto | null; parent: string | null; onClose: () => void; onSaved: () => void }) {
  const levels = useAsync(() => accountsApi.levels(), []);
  const classes = useAsync(() => accountsApi.classifications(), []);
  const parentInfo = useAsync(() => (parent ? accountsApi.get(parent) : Promise.resolve(null)), [parent]);
  const company = useCompany();
  const { values: v, set } = useForm<CreateAccountDto>({
    number: account?.number ?? '', name: account?.name ?? '', parentNumber: account?.parentNumber ?? parent, levelCode: account?.levelCode ?? 1,
    classificationCode: account?.classificationCode ?? 0, currencyId: account?.currencyId ?? company?.company.defaultCurrencyId ?? 0, type: account?.type ?? 'D', nature: account?.nature ?? null,
    isCustomerAccount: account?.isCustomerAccount ?? false, isPettyCash: account?.isPettyCash ?? false,
  });
  const { run, busy, fieldErrors } = useAction();
  // Hija: nivel = nivel de la madre + 1 y misma clasificación (regla del legado).
  const p = parentInfo.data;
  const effectiveLevel = p ? p.levelCode + 1 : v.levelCode;
  const effectiveClass = p && !account ? p.classificationCode : v.classificationCode;

  const save = () => run(async () => {
    if (account) {
      invalidateAccountCache();
      await accountsApi.update(account.number, { name: v.name, classificationCode: effectiveClass, currencyId: v.currencyId, type: v.type, nature: v.nature, isCustomerAccount: v.isCustomerAccount, isPettyCash: v.isPettyCash });
    } else {
      invalidateAccountCache();
      await accountsApi.create({ ...v, levelCode: effectiveLevel, classificationCode: effectiveClass, parentNumber: p ? p.number : null });
    }
    onSaved();
  }, { success: account ? 'Cuenta actualizada.' : 'Cuenta creada.' });

  return (
    <Modal title={account ? `Cuenta ${account.number}` : 'Nueva cuenta'} onClose={onClose}>
      <Form onSubmit={save}>
        {p && <p className="acc-muted">Cuenta madre: <code>{p.number}</code> {p.name}</p>}
        <div className="acc-grid-form">
          <Field label="Número" error={fieldErrors.Number}><Input value={v.number} onChange={(x) => set('number', x)} required maxLength={50} disabled={!!account} /></Field>
          <Field label="Nombre" error={fieldErrors.Name}><Input value={v.name} onChange={(x) => set('name', x)} required maxLength={200} /></Field>
          <Field label="Nivel">
            <Select value={effectiveLevel} onChange={(x) => set('levelCode', x)} disabled={!!account || !!p}
              options={(levels.data?.levels ?? []).map((l) => ({ value: l.code, label: `${l.code} · ${l.description}${l.maxCharacters ? ` (máx. ${l.maxCharacters})` : ''}` }))} />
          </Field>
          <Field label="Clasificación">
            <Select value={effectiveClass || ''} onChange={(x) => set('classificationCode', x)} disabled={!!p && !account} placeholder="Seleccione"
              options={(classes.data ?? []).filter((c) => c.status !== 'I').map((c) => ({ value: c.code, label: c.description }))} />
          </Field>
          <Field label="Moneda"><CurrencySelect value={v.currencyId || null} onChange={(x) => set('currencyId', x)} onlyMapped={false} /></Field>
          <Field label="Tipo">
            <Select value={v.type} onChange={(x) => set('type', x)} options={[{ value: 'M', label: 'Madre (agrupa)' }, { value: 'D', label: 'Detalle (recibe movimientos)' }]} />
          </Field>
          <Field label="Naturaleza">
            <Select value={v.nature ?? ''} onChange={(x) => set('nature', x || null)} placeholder="Sin indicar" options={[{ value: 'D', label: 'Deudora' }, { value: 'C', label: 'Acreedora' }]} />
          </Field>
        </div>
        <Check checked={v.isCustomerAccount} onChange={(x) => set('isCustomerAccount', x)} label="Exige código de cliente en los asientos" />
        <Check checked={v.isPettyCash} onChange={(x) => set('isPettyCash', x)} label="Cuenta de caja chica de la empresa (reemplaza la anterior)" />
        <div className="acc-actions">
          {account && !account.hasChildren && (
            <Button kind="danger" busy={busy} onClick={() => run(async () => { await accountsApi.remove(account.number); invalidateAccountCache(); onSaved(); }, { confirm: `Eliminar la cuenta ${account.number}? Sólo se permite si no tiene movimientos ni plantillas.`, danger: true, success: 'Cuenta eliminada.' })}>Eliminar</Button>
          )}
          <Button onClick={onClose}>Cancelar</Button>
          <Button kind="primary" type="submit" busy={busy}>Guardar</Button>
        </div>
      </Form>
    </Modal>
  );
}
